// TL-6 Arc: 3-point (start, end, bulge). EC-TL-7: collinear third point keeps waiting.
// Measurements at step 3: radius, keeping the side the cursor is on.
import { angleOf, dist, normalizeDeg, sub, type Vec } from "../core/geometry";
import { circumcenter } from "../core/intersect";
import { formatLength, parseScalar } from "../core/units";
import { newId } from "../doc/document";
import type { ArcEntity } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld } from "../engine/renderEntities";
import { drawReadout } from "./line";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

interface ArcParams {
  center: Vec;
  r: number;
  startDeg: number;
  sweepDeg: number;
}

/** Arc through a→c→b endpoints a,b with bulge point c; null when collinear. */
export function arcFrom3Points(a: Vec, b: Vec, c: Vec): ArcParams | null {
  const center = circumcenter(a, b, c);
  if (!center) return null;
  const r = dist(center, a);
  if (r < 1e-9) return null;
  const startDeg = angleOf(sub(a, center));
  const endDeg = angleOf(sub(b, center));
  const bulgeDeg = angleOf(sub(c, center));
  const cw = normalizeDeg(endDeg - startDeg); // positive sweep (CW on screen per ARC-4)
  const bulgeRel = normalizeDeg(bulgeDeg - startDeg);
  const sweepDeg = bulgeRel <= cw ? cw : cw - 360;
  if (Math.abs(sweepDeg) < 1e-9) return null;
  return { center, r, startDeg, sweepDeg };
}

/** Arc with chord a→b and radius r on the side of `side`; null when r < half-chord. */
export function arcFromRadius(a: Vec, b: Vec, r: number, side: Vec): ArcParams | null {
  const chord = dist(a, b);
  if (chord < 1e-9 || r < chord / 2 - 1e-12) return null;
  const h2 = r * r - (chord / 2) * (chord / 2);
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const n = { x: -(b.y - a.y) / chord, y: (b.x - a.x) / chord };
  const sideSign = Math.sign((side.x - mid.x) * n.x + (side.y - mid.y) * n.y) || 1;
  // bulge apex sits (r - h) beyond the chord midpoint, on the cursor's side
  const bulge = { x: mid.x + sideSign * n.x * (r - h), y: mid.y + sideSign * n.y * (r - h) };
  return arcFrom3Points(a, b, bulge);
}

export class ArcTool implements Tool {
  readonly id = "arc" as const;
  private a: Vec | null = null;
  private b: Vec | null = null;
  private cursor: Vec | null = null;
  private collinearWarned = false;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.a !== null;
  }

  private reset(): void {
    this.a = null;
    this.b = null;
    this.cursor = null;
    this.collinearWarned = false;
    this.ctx.setSnapBase(null);
    this.ctx.setSelfClose(null); // belt-and-braces: never inherit another tool's close point
    this.ctx.setMeasure("radius", "radius (at step 3)");
    this.ctx.setHint("Arc: click the start point · Esc = cancel");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.a) {
      this.a = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Arc: click the end point");
      this.ctx.setCursorTip("end point");
    } else if (!this.b) {
      if (dist(e.point, this.a) < 1e-9) return; // EC-TL-5
      this.b = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Arc: click to set the bulge — type a radius ⏎");
      this.ctx.setCursorTip("set bulge · radius ⏎");
    } else {
      const params = arcFrom3Points(this.a, this.b, e.point);
      if (!params) {
        this.collinearWarned = true;
        this.ctx.setHint("Arc: move off the straight line between the points (EC-TL-7)");
        return;
      }
      this.commit(params);
    }
    this.ctx.requestRender();
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    // the collinear warning recovers as soon as the cursor leaves the straight line
    if (this.collinearWarned && this.a && this.b && arcFrom3Points(this.a, this.b, e.point)) {
      this.collinearWarned = false;
      this.ctx.setHint("Arc: click to set the bulge — type a radius ⏎");
    }
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.a) {
      this.reset();
      return true;
    }
    if ((e.key === "Backspace" || e.key === "Delete") && (this.a || this.b)) {
      if (this.b) this.b = null;
      else this.a = null;
      this.ctx.requestRender();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    if (!this.a || !this.b) return false;
    const { units } = this.ctx.docUnits();
    const r = parseScalar(raw, units);
    if (r === null || r <= 1e-9) return false;
    const side = this.cursor ?? { x: (this.a.x + this.b.x) / 2 + 1, y: (this.a.y + this.b.y) / 2 };
    const params = arcFromRadius(this.a, this.b, r, side);
    if (!params) return false; // radius smaller than half the chord
    this.commit(params);
    return true;
  }

  private commit(params: ArcParams): void {
    const doc = this.ctx.store.doc;
    const entity: ArcEntity = {
      id: newId(),
      type: "arc",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      center: params.center,
      r: params.r,
      startDeg: params.startDeg,
      sweepDeg: params.sweepDeg,
      style: structuredClone(doc.defaultStyle)
    };
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
    const entityId = entity.id;
    const a = { ...(this.a as Vec) };
    const b = { ...(this.b as Vec) };
    const prev = { center: params.center, r: params.r, startDeg: params.startDeg, sweepDeg: params.sweepDeg };
    this.ctx.registerReapply((rawValue) => {
      const { units } = this.ctx.docUnits();
      const nr = parseScalar(rawValue, units);
      if (nr === null || nr <= 1e-9) return false;
      const bulgeSide = arcMidpoint(prev);
      const np = arcFromRadius(a, b, nr, bulgeSide);
      if (!np) return false;
      this.ctx.store.dispatch({
        kind: "updateEntities",
        patches: [
          {
            id: entityId,
            before: { ...prev },
            after: { center: np.center, r: np.r, startDeg: np.startDeg, sweepDeg: np.sweepDeg }
          }
        ]
      });
      return true;
    });
    this.reset();
  }

  renderDynamic(v: RenderView): void {
    const doc = this.ctx.store.doc;
    const { ctx } = v;
    ctx.strokeStyle = doc.defaultStyle.strokeColor;
    ctx.lineWidth = strokeWidthWorld(doc.defaultStyle, v.cam, v.dpr);
    if (this.a && this.b && this.cursor) {
      const params = arcFrom3Points(this.a, this.b, this.cursor);
      if (params) {
        const a0 = (params.startDeg * Math.PI) / 180;
        const a1 = ((params.startDeg + params.sweepDeg) * Math.PI) / 180;
        ctx.beginPath();
        ctx.arc(params.center.x, params.center.y, params.r, a0, a1, params.sweepDeg < 0);
        ctx.stroke();
        const { units, precision } = this.ctx.docUnits();
        drawReadout(v, this.cursor, `R ${formatLength(params.r, units, precision)}`);
        return;
      }
    }
    if (this.a) {
      const to = this.b ?? this.cursor;
      if (to) {
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(this.a.x, this.a.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }
}

function arcMidpoint(p: ArcParams): Vec {
  const midDeg = ((p.startDeg + p.sweepDeg / 2) * Math.PI) / 180;
  return { x: p.center.x + p.r * Math.cos(midDeg), y: p.center.y + p.r * Math.sin(midDeg) };
}
